pds-tab {
  align-items: center;
  display: inline-flex;
  position: relative;

  .pds-tabs--pill & {
    flex: 1;
  }
}

///
/// Resets base button styles
///
@mixin pds-button-style-reset {
  appearance: none;
  background-color: transparent;
  border: var(--pine-border-width-none);
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  padding: var(--pine-dimension-none);
}

.pds-tab {
  @include pds-button-style-reset();

  align-items: center;
  color: var(--pine-color-text-secondary);
  display: inline-flex;
  position: relative;
  text-decoration: none;
  white-space: nowrap;

  &.is-active,
  &[aria-selected='true'] {
    color: var(--pine-color-text);

    .pds-tab__content::after {
      opacity: 1;
    }
  }

  &:hover:not(:disabled) {
    color: var(--pine-color-text);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: var(--pine-outline-focus);
    outline-offset: var(--pine-border-width);
  }

  &:disabled,
  &.is-disabled {
    color: var(--pine-color-text-disabled);
    cursor: not-allowed;
  }

  .pds-tab__content {
    font-family: var(--pine-font-family-body);
    font-size: var(--pine-font-size);
    font-weight: var(--pine-font-weight-medium);
    letter-spacing: var(--pine-letter-spacing);
    line-height: var(--pine-line-height-body);
    padding-block-end: 11px;
    position: relative;

    &::after {
      background-color: var(--pine-color-brand);
      content: '';
      height: 3px;
      inset-block-end: var(--pine-dimension-none);
      inset-inline-end: var(--pine-dimension-none);
      inset-inline-start: var(--pine-dimension-none);
      opacity: 0;
      position: absolute;
    }
  }

  .pds-tab-edge {
    display: none;
  }
}

.pds-tabs--availability {
  --color-background-tab: transparent;

  .pds-tab {
    background-color: var(--color-background-tab);
    border-radius: calc(var(--pine-dimension-xs) * 1.25) calc(var(--pine-dimension-xs) * 1.25) var(--pine-dimension-none) var(--pine-dimension-none);
    color: var(--pine-color-text-secondary);
    padding: 8px 16px 13px;

    &.is-active,
    &[aria-selected='true'] {
      --color-background-tab: var(--pine-color-secondary);
      color: var(--pine-color-text);

      .pds-tab__content::after {
        opacity: 1;
      }

      .pds-tab-edge {
        z-index: var(--pine-z-index-raised);
      }
    }

    &:hover:not(.is-active, [aria-selected='true'], :disabled) {
      --color-background-tab: var(--pine-color-secondary-hover);
      color: var(--pine-color-text);
    }

    &:focus-visible {
      color: var(--pine-color-text-secondary);
    }

    &:disabled,
    &.is-disabled {
      --color-background-tab: transparent;
      color: var(--pine-color-text-disabled);
    }
  }

  .pds-tab__content {
    padding-block-end: 7px;
  }

  .pds-tab-edge {
    bottom: 0;
    display: block;
    height: 8px;
    inset-inline-end: 100%;
    overflow: hidden;
    position: absolute;
    width: 8px;

    &::before {
      border-radius: 50%;
      bottom: 0;
      box-shadow: 4px 4px 0 0 var(--color-background-tab);
      content: '';
      display: block;
      height: 200%;
      inset-inline-end: 0;
      position: absolute;
      width: 200%;
    }
  }

  .pds-tab-edge--end {
    inset-inline-end: unset;
    inset-inline-start: 100%;

    &::before {
      box-shadow: -4px 4px 0 0 var(--color-background-tab);
      inset-inline-end: unset;
      inset-inline-start: 0;
    }
  }
}

.pds-tabs--filter {
  .pds-tab {
    background-color: var(--pine-color-background-muted);
    border-radius: 50px;
    color: var(--pine-color-text);
    padding: 6px 15px;

    &.is-active,
    &[aria-selected='true'] {
      background-color: var(--pine-color-primary);
      color: var(--pine-color-text-primary);
    }

    &.is-active:hover:not(:disabled),
    &[aria-selected='true']:hover:not(:disabled) {
      background-color: var(--pine-color-primary-hover);
    }

    &:hover:not(.is-active, [aria-selected='true'], :disabled) {
      background-color: var(--pine-color-border-disabled);
    }

    &:disabled,
    &.is-disabled {
      background-color: transparent;
      color: var(--pine-color-text-disabled);

      &.is-active,
      &[aria-selected='true'] {
        background-color: var(--pine-color-primary-disabled);
      }
    }
  }

  .pds-tab__content {
    padding-block-end: var(--pine-dimension-none);

    &::after {
      content: unset;
    }
  }
}

.pds-tabs--pill {
  --color-background-tab: transparent;

  .pds-tab {
    background-color: var(--color-background-tab);
    border: var(--pine-border-width-thin) solid transparent;
    border-radius: var(--pine-dimension-xs);
    color: var(--pine-color-text-muted);
    flex: 1;
    height: 34px;
    justify-content: center;

    .pds-tab__content {
      align-items: center;
      display: flex;
      justify-content: center;
      padding-block-end: 0;
    }

    &.is-active,
    &[aria-selected='true'] {
      --color-background-tab: var(--pine-color-background-container);
      border-color: var(--pine-color-border);
      box-shadow: var(--pine-box-shadow-100);
      color: var(--pine-color-text-active);

      &:focus-visible {
        border-color: var(--color-border-focus);
        outline: var(--pine-outline-focus);
        outline-offset: var(--pine-border-width);
      }

      /* stylelint-disable max-nesting-depth */
      .pds-tab__content {
        &::after {
          opacity: 0;
        }
      }
      /* stylelint-enable max-nesting-depth */
    }

    &:disabled,
    &.is-disabled {
      color: var(--pine-color-text-disabled);
    }
  }
}
